import React, { useState, useEffect } from 'react';
import { Plus, ChevronDown, Search, Clock, Edit, Trash2, Shield } from 'lucide-react';
import CreateServiceModal from '../components/CreateServiceModal';
import ServiceDetailsModal from '../components/ServiceDetailsModal';
import CategoryModal from '../components/CategoryModal';
import { getServices, getServiceCategories, Service, ServiceCategory, deleteServiceCategory, deleteService } from '../lib/services';
import { useBusiness } from '../contexts/BusinessContext';
import PermissionGuard from '../components/PermissionGuard';


export default function Services() {
  const { selectedBusiness } = useBusiness();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateServiceModalOpen, setIsCreateServiceModalOpen] = useState(false);
  const [isServiceDetailsModalOpen, setIsServiceDetailsModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | null>(null);
  const [categoryModalMode, setCategoryModalMode] = useState<'create' | 'edit'>('create');
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const [servicesData, categoriesData] = await Promise.all([
        getServices(selectedBusiness.id),
        getServiceCategories(selectedBusiness.id)
      ]);
      
      console.log('Loaded data:', {
        services: servicesData.length,
        categories: categoriesData.length
      });
      
      setServices(servicesData);
      setCategories(categoriesData);
      setError(null);
    } catch (err) {
      console.error('Error loading services:', err);
      setError(err instanceof Error ? err.message : 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedBusiness?.id]);

  // Removed all persistence systems
  // No need for duplicate timer systems here

  // Memoize grouped services by category
  const servicesByCategory = React.useMemo(() => categories.map(category => ({
    ...category,
    services: services.filter(service => service.category_id === category.id)
  })), [categories, services]);

  // Memoize filtered categories
  const filteredCategories = React.useMemo(() => {
    const searchLower = searchQuery.toLowerCase();
    return servicesByCategory.filter(category => {
      // Check if category name matches
      if (category.name.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Check if any service in the category matches
      return category.services.some(service =>
        service.name.toLowerCase().includes(searchLower) ||
        service.description?.toLowerCase().includes(searchLower)
      );
    }).map(category => ({ 
      ...category,
      // Filter services within the category
      services: category.services.filter(service =>
        service.name.toLowerCase().includes(searchLower) ||
        service.description?.toLowerCase().includes(searchLower)
      )
    }));
  }, [searchQuery, servicesByCategory]);

  // Auto-expand categories with matching items when searching
  useEffect(() => {
    if (searchQuery) {
      setExpandedCategories(new Set(filteredCategories.map(c => c.id)));
    } else {
      setExpandedCategories(new Set());
    }
  }, [searchQuery, filteredCategories]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };


  const handleDeleteCategory = async (e: React.MouseEvent, categoryId: string) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this category? This will also delete all services in this category.')) {
      return;
    }

    try {
      await deleteServiceCategory(categoryId);
      await loadData();
    } catch (err) {
      console.error('Error deleting category:', err);
      alert('Failed to delete category. Please try again.');
    }
  };


  const handleServiceCreated = async () => {
    await loadData();
    setIsCreateServiceModalOpen(false);
  };

  // Drag and drop functionality disabled for now
  // const handleDragStart = (e: React.DragEvent, type: 'category' | 'service', id: string) => {
  //   e.stopPropagation();
  // };

  // const handleDragOver = (e: React.DragEvent) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  // };

  // const handleDrop = async (e: React.DragEvent, targetId: string, type: 'category' | 'service') => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   // Drag and drop functionality disabled
  // };

  const handleDeleteService = async (e: React.MouseEvent, serviceId: string) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }

    try {
      await deleteService(serviceId);
      await loadData();
    } catch (err) {
      console.error('Error deleting service:', err);
      alert('Failed to delete service. Please try again.');
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-10rem)]">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 shadow-lg text-center">
          <div className="text-red-600 mb-2">Error loading services</div>
          <div className="text-red-800 font-medium">{error}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-10rem)]">
        <div className="backdrop-blur-xl bg-red-50/90 border border-red-200/50 rounded-2xl p-6 shadow-lg text-center"
             style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Services</h2>
          <p className="text-red-600 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <PermissionGuard permission="services.pageAccess">
      <div className="min-h-screen bg-white" data-services-page>
        <div className="w-full px-4 py-4 sm:px-6 sm:py-6">
          <div className="space-y-6">
            {/* Header */}
            <div className="space-y-4">
              {/* Title and Search Row */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h1 className="text-2xl font-semibold text-gray-900">Services</h1>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search services"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              {/* Action Buttons Row */}
              <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                <button
                  onClick={() => {
                    setSelectedCategory(null);
                    setCategoryModalMode('create');
                    setIsCategoryModalOpen(true);
                  }}
                  className="flex items-center justify-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Category</span>
                </button>
                <button 
                  onClick={() => setIsCreateServiceModalOpen(true)}
                  className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  <span>Create Service</span>
                </button>
              </div>
            </div>

            {/* Main Content Container */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                    <p className="mt-4 text-sm text-gray-600">Loading services...</p>
                  </div>
                </div>
              ) : filteredCategories.length === 0 ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Shield className="w-6 h-6 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No services found</h3>
                    <p className="text-gray-500 mb-4">
                      {searchQuery ? 'Try adjusting your search terms' : 'Start by creating your first service'}
                    </p>
                    <button
                      onClick={() => setIsCreateServiceModalOpen(true)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      Create Service
                    </button>
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredCategories.map((category) => (
                    <div key={category.id} className="p-4 sm:p-6">
                      {/* Category Header */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => toggleCategory(category.id)}
                            className="flex items-center gap-2 text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors"
                          >
                            <ChevronDown 
                              className={`h-5 w-5 transition-transform duration-200 ${
                                expandedCategories.has(category.id) ? 'rotate-180' : ''
                              }`} 
                            />
                            {category.name}
                          </button>
                          <span className="bg-gray-100 text-gray-600 text-sm font-medium px-2.5 py-0.5 rounded-full">
                            {category.services.length}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedCategory(category);
                              setCategoryModalMode('edit');
                              setIsCategoryModalOpen(true);
                            }}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-150"
                            title="Edit category"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              handleDeleteCategory(e, category.id);
                            }}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-150"
                            title="Delete category"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {/* Services List */}
                      {expandedCategories.has(category.id) && (
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          {category.services.map((service) => (
                            <div
                              key={service.id}
                              className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4 hover:bg-gray-100 transition-colors cursor-pointer"
                              onClick={() => {
                                setSelectedService(service);
                                setIsServiceDetailsModalOpen(true);
                              }}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <h4 className="font-medium text-gray-900 flex-1">{service.name}</h4>
                                <div className="flex items-center gap-2 ml-2">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Active
                                  </span>
                                </div>
                              </div>
                              
                              {service.description && (
                                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{service.description}</p>
                              )}
                              
                              <div className="space-y-3">
                                {/* Primary Price/Duration Display */}
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-4 text-sm text-gray-500">
                                    {service.price_options && service.price_options.length > 0 && (
                                      <>
                                        <div className="flex items-center gap-1">
                                          <Clock className="h-4 w-4" />
                                          {service.price_options[0].duration_minutes} min
                                        </div>
                                        <div className="font-medium text-gray-900">
                                          ${service.price_options[0].price}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedService(service);
                                        setIsServiceDetailsModalOpen(true);
                                      }}
                                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors duration-150"
                                      title="Edit service"
                                    >
                                      <Edit className="h-3 w-3" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        handleDeleteService(e, service.id);
                                      }}
                                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors duration-150"
                                      title="Delete service"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  </div>
                                </div>

                                {/* All Price Options */}
                                {service.price_options && service.price_options.length > 0 && (
                                  <div className="border-t border-gray-100 pt-3">
                                    <div className="text-xs sm:text-xs font-medium text-gray-500 mb-2">Price Options</div>
                                    <div className="space-y-1.5 sm:space-y-1">
                                      {service.price_options.map((option, index) => (
                                        <div key={index} className="flex items-center justify-between text-xs sm:text-xs">
                                          <span className="text-gray-600 truncate pr-2">
                                            {option.name || `Option ${index + 1}`}
                                          </span>
                                          <div className="flex items-center gap-2 flex-shrink-0">
                                            <span className="text-gray-500">
                                              {option.duration_minutes} min
                                            </span>
                                            <span className="font-medium text-gray-900">
                                              ${option.price}
                                            </span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <CreateServiceModal 
          isOpen={isCreateServiceModalOpen}
          onClose={() => setIsCreateServiceModalOpen(false)}
          onSuccess={handleServiceCreated}
          businessId={selectedBusiness?.id}
          categories={categories}
        />

        <ServiceDetailsModal
          isOpen={isServiceDetailsModalOpen}
          onClose={() => {
            setIsServiceDetailsModalOpen(false);
            setSelectedService(null);
          }}
          service={selectedService}
          onUpdate={loadData}
        />

        <CategoryModal
          isOpen={isCategoryModalOpen}
          onClose={() => {
            setIsCategoryModalOpen(false);
            setSelectedCategory(null);
          }}
          onSuccess={() => {
            setIsCategoryModalOpen(false);
            setSelectedCategory(null);
            loadData();
          }}
          businessId={selectedBusiness?.id}
          category={selectedCategory}
          mode={categoryModalMode}
        />
      </div>
    </PermissionGuard>
  );
}